/**
 * File upload API endpoints and utilities
 */
import { getBaseURL, getHeaders } from "./client.js";

/**
 * Uploads a file to the server with validation
 * @param {File} file - File object to upload
 * @returns {Promise<Object>} Upload result with success status and file data
 */
export const uploadFile = async (file) => {
  try {
    // Validate file on client side
    const allowedTypes = ["image/png", "image/jpeg", "image/jpg", "application/pdf"];
    if (!allowedTypes.includes(file.type)) {
      throw new Error("Please upload a PNG, JPG, or PDF file.");
    }

    // Validate file size
    const maxSize = 5 * 1024 * 1024; // 5MB in bytes
    if (file.size > maxSize) {
      throw new Error("File size must be less than 5MB.");
    }

    // Create FormData for file upload
    const formData = new FormData();
    formData.append("files", file);

    // Upload to Strapi with authentication
    const response = await fetch(`${getBaseURL()}/api/upload`, {
      method: "POST",
      headers: getHeaders(false), // Don't include Content-Type for FormData
      body: formData,
    });

    if (!response.ok) {
      if (response.status === 429) {
        const retryAfter = response.headers.get("Retry-After");
        const minutes = retryAfter ? Math.ceil(retryAfter / 60) : 60;

        throw new Error(`Upload limit reached. Please try again in ${minutes} minutes.`);
      }
      throw new Error(`Upload failed: ${response.status}`);
    }

    const uploadResult = await response.json();

    // Strapi returns an array of uploaded files
    if (uploadResult && uploadResult.length > 0) {
      const uploadedFile = uploadResult[0];
      return {
        success: true,
        file: {
          id: uploadedFile.id,
          name: uploadedFile.name,
          url: uploadedFile.url,
          mime: uploadedFile.mime,
          size: uploadedFile.size,
        },
      };
    } else {
      throw new Error("No file was uploaded");
    }
  } catch (error) {
    return {
      success: false,
      error: error.message,
    };
  }
};
